import React, { useState } from 'react';
import {
  FiCalendar,
  FiXCircle,
  FiRotateCcw,
  FiX,
  FiCheck,
} from 'react-icons/fi';
import { User } from '../../types';
import { showToast } from '../../utils/toast';
import { useApi } from '../../hooks/useApi';
import { useTheme } from '../../hooks/useTheme';
import { isMasterFeeOnlyPlan, isMasterFeeSubscription, isSubscriptionAccessAllowed, isComplimentaryPlan } from '../../utils/subscriptionAccess';
import PaymentDueTimer from '../Common/PaymentDueTimer';

interface PricingPlansProps {
  user: User;
  /** Called after in-app upgrade/change to a platform plan (not Stripe redirect). */
  onPlatformPurchase?: (plan: 'growth' | 'general' | 'pro') => void;
}

type PlanVariant = 'standard' | 'trial';
type PlanKey = 'growth' | 'general' | 'pro';
type ConfirmModalKind = 'upgrade' | 'downgrade' | 'cancel';

interface ConfirmModalState {
  kind: ConfirmModalKind;
  planKey?: PlanKey;
  title: string;
  lines: string[];
  confirmLabel: string;
  isDestructive?: boolean;
}

const PLAN_RANKS: Record<string, number> = { growth: 1, general: 2, pro: 3 };

const TIER_SETUP_CENTS: Record<PlanKey, number> = {
  growth: 0,
  general: 299_900,
  pro: 499_900,
};
const MONTHLY_CENTS: Record<PlanKey, number> = {
  growth: 29_900,
  general: 29_900,
  pro: 39_900,
};

const PLAN_LABELS: Record<PlanKey, string> = {
  growth: 'Growth',
  general: 'General',
  pro: 'Premium',
};

const NO_DOWNGRADE_TO = new Set<PlanKey>(['growth']);

const formatUsd = (cents: number) =>
  `$${Math.round(cents / 100).toLocaleString()}`;

const PLAN_PRICING: Record<
  PlanKey,
  Record<PlanVariant, { primary: string; secondary?: string } | null>
> = {
  growth: {
    standard: { primary: '$199/mo', secondary: 'No upfront · billed monthly via Whop' },
    trial: null,
  },
  general: {
    standard: {
      primary: '$2,999 + $299/mo',
      secondary: 'Billed monthly via Whop',
    },
    trial: null,
  },
  pro: {
    standard: {
      primary: '$4,999 + $399/mo',
      secondary: 'Billed monthly via Whop',
    },
    trial: null,
  },
};

const PLAN_FEATURES: Record<PlanKey, string[]> = {
  growth: [
    'Connect 1 GenesisFX only (Forex)',
    'Follow 1 forex master only',
    'Forex access',
    'Self-serve setup',
  ],
  general: [
    'Connect 1 Webull + 1 GenesisFX',
    'Follow 1 master only',
    'Options and Forex access',
    'Self-serve setup',
  ],
  pro: [
    'Connect up to 5 accounts (Webull or GenesisFX)',
    'Follow multiple masters',
    'Options and Forex access',
    'White-glove setup every time',
  ],
};

const PLAN_TAGLINES: Record<PlanKey, string> = {
  growth: 'Forex starter',
  general: 'Copy trades every day',
  pro: 'Full access',
};

const PLAN_THEMES: Record<
  PlanKey,
  {
    accent: string;
    accentSoft: string;
    accentLight: string;
    border: string;
    headerBg: string;
    headerText: string;
    tabIdle: string;
    tabIdleText: string;
    price: string;
    button: string;
    check: string;
  }
> = {
  growth: {
    accent: '#10b981',
    accentSoft:
      'linear-gradient(180deg, rgba(16,185,129,0.16) 0%, rgba(16,185,129,0.04) 42%, rgba(8,12,18,0.92) 100%)',
    accentLight:
      'linear-gradient(180deg, rgba(16,185,129,0.14) 0%, rgba(255,255,255,0.98) 45%, rgb(248,250,252) 100%)',
    border: 'border-emerald-400/40',
    headerBg: 'bg-emerald-600',
    headerText: 'text-white',
    tabIdle: 'bg-emerald-500/10 dark:bg-emerald-950/50',
    tabIdleText: 'text-emerald-700 dark:text-emerald-300/70',
    price: 'text-emerald-600 dark:text-emerald-300',
    button: 'bg-emerald-600 text-white hover:bg-emerald-500 shadow-[0_0_24px_rgba(16,185,129,0.25)]',
    check: 'text-emerald-400',
  },
  general: {
    accent: '#246BF5',
    accentSoft:
      'linear-gradient(180deg, rgba(36,107,245,0.18) 0%, rgba(36,107,245,0.05) 42%, rgba(8,12,18,0.92) 100%)',
    accentLight:
      'linear-gradient(180deg, rgba(36,107,245,0.13) 0%, rgba(255,255,255,0.98) 45%, rgb(248,250,252) 100%)',
    border: 'border-[#246BF5]/45',
    headerBg: 'bg-[#246BF5]',
    headerText: 'text-white',
    tabIdle: 'bg-blue-500/10 dark:bg-white/8',
    tabIdleText: 'text-blue-700/70 dark:text-white/50',
    price: 'text-[#246BF5] dark:text-white',
    button: 'bg-[#246BF5] text-white hover:bg-[#1d5ad4] shadow-[0_0_24px_rgba(36,107,245,0.28)]',
    check: 'text-[#5b8ef7]',
  },
  pro: {
    accent: '#c9a227',
    accentSoft:
      'linear-gradient(180deg, rgba(201,162,39,0.2) 0%, rgba(201,162,39,0.06) 42%, rgba(8,12,18,0.92) 100%)',
    accentLight:
      'linear-gradient(180deg, rgba(201,162,39,0.16) 0%, rgba(255,255,255,0.98) 45%, rgb(250,250,249) 100%)',
    border: 'border-[#c9a227]/50',
    headerBg: 'bg-[#c9a227]',
    headerText: 'text-black',
    tabIdle: 'bg-[#c9a227]/10 dark:bg-[#c9a227]/20',
    tabIdleText: 'text-[#8a6b10] dark:text-[#c9a227]/80',
    price: 'text-[#9a7610] dark:text-[#e8c547]',
    button: 'bg-[#c9a227] text-black hover:bg-[#d4af37] shadow-[0_0_28px_rgba(201,162,39,0.3)]',
    check: 'text-[#c9a227]',
  },
};

const formatActivePlanLabel = (
  plan: string | null | undefined,
  billingMode?: string | null
) => {
  if (!plan) return '';
  const base = PLAN_LABELS[plan as PlanKey] || plan.charAt(0).toUpperCase() + plan.slice(1);
  return billingMode === 'trial' ? `${base} trial` : base;
};

const Spinner = () => (
  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const PricingPlans: React.FC<PricingPlansProps> = ({ user, onPlatformPurchase }) => {
  const { theme: colorMode } = useTheme();
  // Trial UI disabled on frontend — always use standard
  const [generalVariant] = useState<PlanVariant>('standard');
  const [proVariant] = useState<PlanVariant>('standard');
  // const [generalVariant, setGeneralVariant] = useState<PlanVariant>('standard');
  // const [proVariant, setProVariant] = useState<PlanVariant>('standard');
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState | null>(null);

  const { data: statusData, refetch: refetchStatus } = useApi.useSubscriptionStatus();
  const subscription = statusData?.subscription ?? null;
  const masterFeeSubscription = (statusData as any)?.masterFeeSubscription ?? null;
  const billingPlanKey = String(user.plan || '').toLowerCase();
  const showBillingPaymentTimer =
    !isMasterFeeOnlyPlan(billingPlanKey) &&
    !isComplimentaryPlan(billingPlanKey) &&
    (['growth', 'general', 'pro'].includes(billingPlanKey) ||
      ['growth', 'general', 'pro'].includes(String((statusData as any)?.subscription?.plan || '').toLowerCase()));
  // Trial UI disabled on frontend
  // const trialEligible = statusData?.trialEligible === true;
  // const trialEligibilityReason =
  //   statusData?.trialEligibilityReason ||
  //   'Trial is only available once for new accounts.';
  const trialEligible = false;
  const trialEligibilityReason = 'Trial is currently unavailable.';
  const growthEligible = statusData?.growthEligible !== false;
  const growthEligibilityReason =
    statusData?.growthEligibilityReason ||
    'Growth is not available after you have paid a one-time setup fee.';
  const hasActiveSub = isSubscriptionAccessAllowed(statusData);
  const currentPlan: string | null = hasActiveSub ? subscription?.plan ?? null : null;
  const pendingPlan = subscription?.pendingPlan || null;
  const currentPlanLabel = formatActivePlanLabel(currentPlan, subscription?.billingMode);
  const cancelScheduled = Boolean(subscription?.cancelAtPeriodEnd);
  const trialSetupPending =
    subscription?.billingMode === 'trial' && !subscription?.deferredSetupCharged;
  const trialSetupFeeLabel =
    subscription?.deferredSetupFeeCents && subscription.deferredSetupFeeCents > 0
      ? `$${(subscription.deferredSetupFeeCents / 100).toLocaleString()} setup fee due after 30 days`
      : 'setup fee due after 30 days';

  // useEffect(() => {
  //   if (!trialEligible) {
  //     setGeneralVariant('standard');
  //     setProVariant('standard');
  //   }
  // }, [trialEligible]);

  const createCheckoutSessionMutation = useApi.useCreateCheckoutSession({
    onError: (error) => {
      showToast('error', error.message || 'Failed to start Whop checkout session.');
    },
  });

  const changePlanMutation = useApi.useChangePlan({
    onSuccess: (data, purchasedPlan) => {
      setConfirmModal(null);
      showToast('success', data?.message || 'Plan changed successfully.');
      refetchStatus();
      if (
        purchasedPlan === 'growth' ||
        purchasedPlan === 'general' ||
        purchasedPlan === 'pro'
      ) {
        onPlatformPurchase?.(purchasedPlan);
      }
    },
    onError: (error) => {
      showToast('error', error.message || 'Failed to change plan.');
    },
  });

  const cancelSubscriptionMutation = useApi.useCancelSubscription({
    onSuccess: (data) => {
      setConfirmModal(null);
      showToast('success', data?.message || 'Subscription cancelled.');
      refetchStatus();
    },
  });

  const resumeSubscriptionMutation = useApi.useResumeSubscription({
    onSuccess: (data) => {
      showToast('success', data?.message || 'Subscription resumed.');
      refetchStatus();
    },
  });

  const isConfirmLoading =
    changePlanMutation.isPending || cancelSubscriptionMutation.isPending;

  const handleSubscribeClick = (planKey: PlanKey, variant: PlanVariant) => {
    if (user.role !== 'follower') {
      showToast('error', 'Only follower accounts can purchase subscriptions.');
      return;
    }

    // Trial UI disabled on frontend
    // if (variant === 'trial' && !trialEligible) {
    //   showToast('error', trialEligibilityReason);
    //   return;
    // }
    if (variant === 'trial') {
      showToast('error', trialEligibilityReason);
      return;
    }

    if (planKey === 'growth' && !growthEligible) {
      showToast('error', growthEligibilityReason);
      return;
    }

    // if (planKey === 'growth' && variant === 'trial') {
    //   showToast('error', 'Growth does not offer a trial.');
    //   return;
    // }

    createCheckoutSessionMutation.mutate({
      plan: planKey,
      // billingMode: variant === 'trial' ? 'trial' : 'monthly_instant',
      billingMode: 'monthly_instant',
    });
  };

  const openUpgradeModal = (_planKey: PlanKey) => {
    showToast('info', 'Please contact admin to upgrade or downgrade your plan.');
  };

  const openDowngradeModal = (_planKey: PlanKey) => {
    showToast('info', 'Please contact admin to upgrade or downgrade your plan.');
  };

  const openCancelModal = () => {
    const until = subscription?.endDate
      ? new Date(subscription.endDate).toLocaleDateString()
      : 'the end of your current billing period';

    setConfirmModal({
      kind: 'cancel',
      title: 'Cancel subscription?',
      lines: [
        `You keep full access until ${until}.`,
        'No further charges will be made.',
        'You can resume anytime before that date.',
      ],
      confirmLabel: 'Cancel subscription',
      isDestructive: true,
    });
  };

  const handleConfirmModal = () => {
    if (!confirmModal) return;
    if (confirmModal.kind === 'upgrade' && confirmModal.planKey) {
      changePlanMutation.mutate(confirmModal.planKey);
      return;
    }
    if (confirmModal.kind === 'downgrade' && confirmModal.planKey) {
      changePlanMutation.mutate(confirmModal.planKey);
      return;
    }
    if (confirmModal.kind === 'cancel') {
      cancelSubscriptionMutation.mutate('platform');
    }
  };

  const handleResume = () => {
    if (resumeSubscriptionMutation.isPending) return;
    resumeSubscriptionMutation.mutate('platform');
  };

  const getAction = (planKey: PlanKey, variant: PlanVariant) => {
    const checkoutVars = createCheckoutSessionMutation.variables;
    const isThisCheckoutLoading =
      createCheckoutSessionMutation.isPending &&
      typeof checkoutVars === 'object' &&
      checkoutVars?.plan === planKey;
    const isThisChangeLoading =
      changePlanMutation.isPending && changePlanMutation.variables === planKey;
    const isLoading = isThisCheckoutLoading || isThisChangeLoading;

    const isCurrent = currentPlan === planKey;
    const currentRank = currentPlan ? PLAN_RANKS[currentPlan] || 0 : 0;
    const targetRank = PLAN_RANKS[planKey];
    const cannotDowngradeHere =
      NO_DOWNGRADE_TO.has(planKey) && currentRank > targetRank;
    const growthBlocked = planKey === 'growth' && !growthEligible;

    let label = 'Pay';
    let onClick: (() => void) | undefined = () =>
      handleSubscribeClick(planKey, variant);
    let disabled = isLoading;

    if (growthBlocked && !(hasActiveSub && currentPlan === 'growth')) {
      label = 'Not available';
      onClick = undefined;
      disabled = true;
    } else if (hasActiveSub && currentPlan) {
      if (isCurrent) {
        if (pendingPlan) {
          label = 'Keep plan';
          onClick = () => changePlanMutation.mutate(planKey);
          disabled = isLoading;
        } else {
          label = 'Current plan';
          onClick = undefined;
          disabled = true;
        }
      } else if (pendingPlan === planKey) {
        label = 'Downgrade scheduled';
        onClick = undefined;
        disabled = true;
      } else if (targetRank > currentRank) {
        label = 'Upgrade';
        onClick = () => openUpgradeModal(planKey);
      } else if (cannotDowngradeHere || growthBlocked) {
        label = 'Not available';
        onClick = undefined;
        disabled = true;
      } else {
        label = 'Downgrade';
        onClick = () => openDowngradeModal(planKey);
      }
    }

    return {
      label,
      onClick,
      disabled,
      isLoading,
      isCurrent,
      cannotDowngradeHere,
      growthBlocked,
    };
  };

  const renderPlanColumn = (planKey: PlanKey) => {
    const theme = PLAN_THEMES[planKey];
    // Trial tabs hidden on frontend — standard only
    // const hasTrial = planKey !== 'growth' && trialEligible;
    const hasTrial = false;
    const variant: PlanVariant =
      planKey === 'general'
        ? generalVariant
        : planKey === 'pro'
          ? proVariant
          : 'standard';
    // const setVariant =
    //   planKey === 'general'
    //     ? setGeneralVariant
    //     : planKey === 'pro'
    //       ? setProVariant
    //       : undefined;
    const setVariant = undefined;
    const pricing = PLAN_PRICING[planKey][variant] || PLAN_PRICING[planKey].standard;
    if (!pricing) return null;

    const action = getAction(planKey, 'standard');
    // const trialTabLabel = planKey === 'pro' ? 'Trial Pro' : 'Trial';

    return (
      <div
        key={planKey}
        className={[
          'group relative flex flex-col min-h-[640px] rounded-3xl border overflow-hidden transition-transform duration-300 hover:-translate-y-1',
          theme.border,
          planKey === 'pro'
            ? 'ring-1 ring-[#c9a227]/35 shadow-[0_16px_45px_rgba(201,162,39,0.13)]'
            : 'shadow-[0_14px_40px_rgba(15,23,42,0.10)] dark:shadow-[0_16px_48px_rgba(0,0,0,0.35)]',
        ].join(' ')}
        style={{ background: colorMode === 'dark' ? theme.accentSoft : theme.accentLight }}
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-32 opacity-60"
          style={{
            background: `radial-gradient(ellipse at top, ${theme.accent}33, transparent 70%)`,
          }}
        />

        {/* Trial tabs commented out — standard header only
        {hasTrial && setVariant ? (
          <div className="relative grid grid-cols-2">
            <button
              type="button"
              onClick={() => setVariant('standard')}
              className={[
                'py-4 text-center text-xs sm:text-sm font-bold tracking-[0.14em] uppercase transition-colors',
                variant === 'standard'
                  ? `${theme.headerBg} ${theme.headerText}`
                  : `${theme.tabIdle} ${theme.tabIdleText} hover:opacity-90`,
              ].join(' ')}
            >
              {PLAN_LABELS[planKey]}
            </button>
            <button
              type="button"
              onClick={() => setVariant('trial')}
              className={[
                'py-4 text-center text-xs sm:text-sm font-bold tracking-[0.12em] uppercase transition-colors',
                variant === 'trial'
                  ? `${theme.headerBg} ${theme.headerText}`
                  : `${theme.tabIdle} ${theme.tabIdleText} hover:opacity-90`,
              ].join(' ')}
            >
              {trialTabLabel}
            </button>
          </div>
        ) : (
        */}
        <div
          className={[
            'relative py-4 text-center text-sm font-bold tracking-[0.18em] uppercase',
            theme.headerBg,
            theme.headerText,
          ].join(' ')}
        >
          {PLAN_LABELS[planKey]}
        </div>
        {/* )} */}

        <div className="relative flex flex-1 flex-col px-6 pt-8 pb-8">
          <p className="mb-4 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-muted dark:text-white/40">
            {PLAN_TAGLINES[planKey]}
          </p>

          <div className="mb-8 text-center space-y-2 min-h-[5.5rem]">
            <p className={['text-2xl md:text-[1.65rem] font-semibold tabular-nums tracking-tight', theme.price].join(' ')}>
              {pricing.primary}
            </p>
            {pricing.secondary ? (
              <p className="text-xs text-muted dark:text-white/45 leading-relaxed max-w-[16rem] mx-auto">
                {pricing.secondary}
              </p>
            ) : null}
          </div>

          <ul className="space-y-4 mb-10 flex-1">
            {PLAN_FEATURES[planKey].map((feature) => (
              <li key={feature} className="flex gap-3 text-[14px] font-medium leading-snug text-foreground/80 dark:text-white/80">
                <span
                  className={[
                    'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-black/[0.04] dark:bg-white/5',
                    theme.check,
                  ].join(' ')}
                >
                  <FiCheck className="h-3.5 w-3.5" />
                </span>
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={action.onClick}
            disabled={action.disabled}
            className={[
              'w-full py-4 rounded-2xl text-sm font-bold tracking-wide uppercase transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center',
              action.isCurrent
                ? 'bg-emerald-500/15 border border-emerald-500/35 text-emerald-700 dark:text-emerald-400 shadow-none'
                : theme.button,
            ].join(' ')}
          >
            {action.isLoading ? <Spinner /> : null}
            {action.label}
          </button>

          {action.growthBlocked ? (
            <p className="mt-4 text-[11px] text-center text-muted dark:text-white/35 leading-relaxed">
              Not available after paying a General or Pro one-time fee.
            </p>
          ) : action.cannotDowngradeHere && hasActiveSub ? (
            <p className="mt-4 text-[11px] text-center text-muted dark:text-white/35 leading-relaxed">
              Downgrade to Growth isn&apos;t available on General or Pro.
            </p>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="animate-fadeIn pb-20 px-4 max-w-6xl mx-auto">
      <div className="pt-6 pb-10 space-y-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#246BF5]">
          Tradepilot AI
        </p>
        <h2 className="text-4xl md:text-5xl font-semibold text-foreground tracking-tight">
          Billing Plans
        </h2>
        <p className="text-base md:text-lg text-muted font-light max-w-xl">
          Start on Growth, or unlock more with General and Pro. Upgrade anytime — pay only the remaining difference.
        </p>
      </div>

      {showBillingPaymentTimer && (
        <div className="mb-6 space-y-3">
          {isMasterFeeSubscription(masterFeeSubscription) &&
            masterFeeSubscription?.endDate &&
            (masterFeeSubscription.isCurrentlyActive === true ||
              masterFeeSubscription.isActive === true ||
              masterFeeSubscription.status === 'active') && (
              <PaymentDueTimer
                endDate={masterFeeSubscription.endDate}
                daysRemaining={masterFeeSubscription.daysRemaining}
                label="Master fee due in"
              />
            )}
          {hasActiveSub && subscription?.endDate && (
            <PaymentDueTimer
              endDate={subscription.endDate}
              daysRemaining={subscription.daysRemaining}
              label={
                cancelScheduled
                  ? 'Plan access ends in'
                  : trialSetupPending
                    ? 'Trial / next billing in'
                    : 'Next billing payment in'
              }
            />
          )}
        </div>
      )}

      {hasActiveSub && subscription && (
        <div className="mb-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-xl border border-border bg-surface-elevated px-5 py-4 shadow-sm">
          <div>
            <p className="text-sm text-foreground">
              You&apos;re on{' '}
              <span className="text-[#c9a227] font-semibold">{currentPlanLabel}</span>
            </p>
            {pendingPlan ? (
              <p className="mt-1 text-xs text-amber-300/90">
                Your plan will downgrade to{' '}
                <span className="font-semibold capitalize">{pendingPlan}</span>
                {subscription.pendingPlanEffectiveAt || subscription.endDate
                  ? ` on ${new Date(
                    subscription.pendingPlanEffectiveAt || subscription.endDate
                  ).toLocaleDateString()}`
                  : ' next month'}
                . Next charge:{' '}
                {formatUsd(
                  Number(subscription.recurringAmount) ||
                  MONTHLY_CENTS[(pendingPlan as PlanKey) || 'general']
                )}
                /mo.
              </p>
            ) : null}
            <p className="mt-1 text-xs text-muted flex items-center gap-1.5">
              <FiCalendar className="h-3 w-3" />
              {cancelScheduled
                ? `Access ends ${new Date(subscription.endDate).toLocaleDateString()}`
                : trialSetupPending
                  ? `Next billing ${new Date(subscription.endDate).toLocaleDateString()} · ${trialSetupFeeLabel}`
                  : !pendingPlan
                    ? `Next billing ${new Date(subscription.endDate).toLocaleDateString()} · ${formatUsd(
                        Number(subscription.whopRenewalAmountCents) ||
                          Number(subscription.recurringAmount) ||
                          MONTHLY_CENTS[(currentPlan as PlanKey) || 'general']
                      )}/mo`
                    : `Next billing ${new Date(subscription.endDate).toLocaleDateString()}`}
            </p>
          </div>
          {cancelScheduled ? (
            <button
              onClick={handleResume}
              disabled={resumeSubscriptionMutation.isPending}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-[#c9a227] text-black hover:bg-[#d4af37] disabled:opacity-50"
            >
              {resumeSubscriptionMutation.isPending ? <Spinner /> : <FiRotateCcw className="h-4 w-4" />}
              Resume
            </button>
          ) : (
            <button
              onClick={openCancelModal}
              disabled={cancelSubscriptionMutation.isPending}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-muted hover:text-red-600 hover:border-red-500/40 dark:hover:text-red-400 disabled:opacity-50"
            >
              {cancelSubscriptionMutation.isPending ? <Spinner /> : <FiXCircle className="h-4 w-4" />}
              Cancel
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
        {renderPlanColumn('growth')}
        {renderPlanColumn('general')}
        {renderPlanColumn('pro')}
      </div>

      {confirmModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="billing-confirm-title"
            className="w-full max-w-md rounded-2xl border border-border bg-surface-elevated shadow-2xl"
          >
            <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
              <h3 id="billing-confirm-title" className="text-lg font-semibold text-foreground">
                {confirmModal.title}
              </h3>
              <button
                type="button"
                onClick={() => !isConfirmLoading && setConfirmModal(null)}
                className="rounded-lg p-1 text-muted hover:bg-surface hover:text-foreground"
                aria-label="Close"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3 px-5 py-5">
              {confirmModal.lines.map((line) => (
                <p key={line} className="text-sm leading-relaxed text-muted">
                  {line}
                </p>
              ))}
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-border px-5 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={isConfirmLoading}
                onClick={() => setConfirmModal(null)}
                className="rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-muted hover:bg-surface disabled:opacity-50"
              >
                Go back
              </button>
              <button
                type="button"
                disabled={isConfirmLoading}
                onClick={handleConfirmModal}
                className={[
                  'inline-flex items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold disabled:opacity-50',
                  confirmModal.isDestructive
                    ? 'bg-red-500/90 text-white hover:bg-red-500'
                    : confirmModal.kind === 'upgrade'
                      ? 'bg-[#c9a227] text-black hover:bg-[#d4af37]'
                      : 'bg-[#246BF5] text-white hover:bg-[#1d5ad4]',
                ].join(' ')}
              >
                {isConfirmLoading ? <Spinner /> : null}
                {confirmModal.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PricingPlans;
